import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Plus,
  Webhook,
  Trash2,
  MoreHorizontal,
  Search,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { useWebhooks, useDeleteWebhook } from "@/hooks/use-webhooks"
import type { Webhook as WebhookType } from "@/lib/types"

export function WebhooksListPage() {
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")

  const filters =
    statusFilter === "active"
      ? { is_active: true, search: search || undefined }
      : statusFilter === "inactive"
        ? { is_active: false, search: search || undefined }
        : { search: search || undefined }

  const { data, isLoading } = useWebhooks(filters)
  const deleteMutation = useDeleteWebhook()

  const webhooks = data?.data ?? []

  const handleDelete = (wh: WebhookType) => {
    if (!confirm(`Usunąć webhook "${wh.name}"?`)) return
    deleteMutation.mutate(wh.id, {
      onSuccess: () => toast.success("Webhook usunięty"),
    })
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            Webhooki
          </h1>
          <p className="text-sm mt-1 text-muted-foreground">
            Konfiguruj powiadomienia HTTP o zdarzeniach w systemie
          </p>
        </div>
        <Button onClick={() => navigate("/webhooks/new")}>
          <Plus size={16} />
          Nowy webhook
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Szukaj webhook..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-border bg-card text-sm text-foreground"
          />
        </div>
        <div className="flex gap-1 rounded-lg p-1 bg-muted">
          {[
            { key: "all", label: "Wszystkie" },
            { key: "active", label: "Aktywne" },
            { key: "inactive", label: "Nieaktywne" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === f.key
                  ? "bg-card text-foreground"
                  : "text-muted-foreground"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 rounded-lg animate-pulse bg-muted"
            />
          ))}
        </div>
      ) : webhooks.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 rounded-lg border border-dashed">
          <Webhook size={48} className="text-muted-foreground" />
          <p className="mt-4 text-lg font-medium text-foreground">
            Brak webhooków
          </p>
          <p className="text-sm mt-1 text-muted-foreground">
            Utwórz pierwszy webhook, by otrzymywać powiadomienia HTTP
          </p>
          <Button onClick={() => navigate("/webhooks/new")} className="mt-4">
            <Plus size={16} />
            Nowy webhook
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map((wh) => (
            <WebhookCard
              key={wh.id}
              webhook={wh}
              onEdit={() => navigate(`/webhooks/${wh.id}`)}
              onDelete={() => handleDelete(wh)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function WebhookCard({
  webhook,
  onEdit,
  onDelete,
}: {
  webhook: WebhookType
  onEdit: () => void
  onDelete: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)

  return (
    <div
      className="rounded-lg border p-4 transition-colors cursor-pointer hover:shadow-sm bg-card"
      onClick={onEdit}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${
              webhook.is_active ? "bg-green-500/10" : ""
            }`}
          >
            {webhook.is_active ? (
              <Webhook size={20} className="text-green-500" />
            ) : (
              <Webhook size={20} className="text-muted-foreground" />
            )}
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-medium truncate text-foreground">
              {webhook.name}
            </h3>
            <div className="flex items-center gap-2 mt-0.5">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  webhook.is_active
                    ? "bg-green-500/10 text-green-500"
                    : "text-muted-foreground"
                }`}
              >
                {webhook.is_active ? "Aktywny" : "Nieaktywny"}
              </span>
              <span className="text-xs truncate max-w-[300px] text-muted-foreground">
                {webhook.url}
              </span>
              <span className="text-xs text-muted-foreground">
                {webhook.subscribed_events.length} zdarzeń
              </span>
              {webhook.failure_count > 0 && (
                <span className="flex items-center gap-1 text-xs text-red-500">
                  <AlertTriangle size={12} />
                  {webhook.failure_count} błędów
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          <StatusIcon status={webhook.failure_count > 0 ? "warning" : webhook.is_active ? "active" : "inactive"} />
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 rounded-md transition-colors hover:bg-muted text-muted-foreground"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 w-40 rounded-lg border shadow-lg py-1 bg-card">
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      onEdit()
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted text-foreground"
                  >
                    Szczegóły
                  </button>
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      onDelete()
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted text-red-500"
                  >
                    <span className="flex items-center gap-2">
                      <Trash2 size={14} />
                      Usuń
                    </span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function StatusIcon({ status }: { status: "active" | "inactive" | "warning" }) {
  if (status === "active") return <CheckCircle2 size={16} className="text-green-500" />
  if (status === "warning") return <AlertTriangle size={16} className="text-amber-500" />
  return <XCircle size={16} className="text-muted-foreground" />
}
